import {
  MASKED_VALUE,
  type DataflowDetail,
  type DataflowFieldInput,
} from "@shared/types/dataflows";

/**
 * A connector field's value between the form and the wire, shared by the connection editor and
 * the setup page (`pages/connections/setup.tsx`, ADR 0009 section 7). Nothing here ever reads a
 * stored credential: it arrives as `MASKED_VALUE` and leaves as that same sentinel (FR-43).
 */

/**
 * The wire allows `string | number | boolean | null` per field
 * (`dataflowFieldInputSchema`), so a multi-select or a key/value list travels
 * as JSON text rather than as an array that the schema would reject with a 422
 * the user cannot act on.
 */
export function encodeFieldValue(value: unknown): DataflowFieldInput["value"] {
  if (value === undefined || value === null) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (typeof File !== "undefined" && value instanceof File) {
    // Only a base64-encoded file has a value that can be posted; a raw handle
    // cannot be serialised, and sending its name would store a lie.
    return null;
  }
  return JSON.stringify(value);
}

function decodeFieldValue(raw: string | null, controlType: string): unknown {
  if (raw === null) return controlType === "checkbox" || controlType === "toggle" ? false : "";
  if (controlType === "checkbox" || controlType === "toggle") return raw === "true" || raw === "1";
  if (controlType === "multiselect" || controlType === "keyvalue") {
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }
  return raw;
}

export function savedFieldValue(
  saved: DataflowDetail["fields"][number],
  controlType: string,
): unknown {
  // FR-43: a credential reads back as the sentinel, never as a value.
  if (saved.isCredential) return saved.hasStoredSecret ? MASKED_VALUE : "";
  return decodeFieldValue(saved.value, controlType);
}
